import type { SerializeFrom } from '@remix-run/server-runtime'
import { useContext, useLayoutEffect, useRef } from 'react'

import type { Collection } from '~/models/collection.server'
import type { ProductFull } from '~/models/product.server'

import type { ShopReorderContextType } from './ShopReorderContext'
import { ShopReorderContext } from './ShopReorderContext'

interface ShopReorderDataProviderProps<T> {
  data: SerializeFrom<T>[]
  type: 'products' | 'collections'
  children: (context: ShopReorderContextType) => React.ReactNode
}

export function ShopReorderDataProvider<T>({
  data,
  type,
  children,
}: ShopReorderDataProviderProps<T>) {
  const context = useContext(ShopReorderContext)
  const previousDataRef = useRef<SerializeFrom<T>[] | null>(null)

  if (!context) {
    throw new Error(
      'ShopReorderDataProvider must be used within ShopReorderProvider'
    )
  }

  useLayoutEffect(() => {
    // Only update if the data has actually changed
    const hasDataChanged =
      previousDataRef.current === null ||
      data.length !== previousDataRef.current.length ||
      data.some((item, index) => {
        const prevItem = previousDataRef.current?.[index]
        return !prevItem || JSON.stringify(item) !== JSON.stringify(prevItem)
      })

    if (hasDataChanged) {
      previousDataRef.current = data

      if (type === 'products') {
        context.setInitialProducts(data as SerializeFrom<ProductFull>[])
      } else {
        context.setInitialCollections(data as SerializeFrom<Collection>[])
      }
    }
  }, [data, type, context])

  return <>{children(context)}</>
}
